import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal, touch } from './Modal';
import { OrderHistoryDialog } from './OrderHistoryDialog';
import { CancelLineDialog } from './BinsBoard';
import {
  apiMessage,
  fmtDay,
  fmtEur,
  fmtTime,
  receivingApi,
  type Alternative,
  type CallEntry,
  type DecisionOutcome,
} from '../../lib/receiving';
import { cn } from '../../utils/cn';

/**
 * The call list (spec §7). One entry per short line per close. The person who took the order is
 * the one who calls back; the customer decides; the employee records the decision with the
 * customer's explicit agreement — a tick naming them. A decision is final.
 */
export function CallListView({ meName }: { meName: string }) {
  const { t } = useTranslation();
  const [list, setList] = useState<CallEntry[] | null>(null);
  const [error, setError] = useState('');
  const [deciding, setDeciding] = useState<{
    entry: CallEntry;
    kind: 'WAIT' | 'SWITCH' | 'CANCEL';
  } | null>(null);
  const [outcome, setOutcome] = useState<{ entry: CallEntry; out: DecisionOutcome } | null>(null);
  const [history, setHistory] = useState<{ id: string; number: string } | null>(null);
  const [partCancel, setPartCancel] = useState<CallEntry | null>(null);

  const load = useCallback(() => {
    receivingApi
      .callList()
      .then((r) => setList(r.data))
      .catch((err) => setError(apiMessage(err)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="text-lg text-destructive">{error}</p>;
  if (!list) return <p className="text-lg text-muted-foreground">{t('rcv.loading')}</p>;
  if (list.length === 0)
    return (
      <p className="rounded-xl border border-dashed p-6 text-lg text-muted-foreground">
        {t('rcv.calls.empty')}
      </p>
    );

  return (
    <div className="space-y-3">
      {outcome && (
        <div className="rounded-xl border-2 border-emerald-600/60 bg-emerald-500/5 p-4 text-base">
          <p className="font-semibold">
            {outcome.entry.orderNumber} · {outcome.entry.productName}:{' '}
            {t(`rcv.calls.decision.${outcome.out.decision}`)}
          </p>
          {outcome.out.docs.map((d, i) => (
            <p key={i}>
              <strong>{d.number}</strong> · {d.text}
            </p>
          ))}
          {outcome.out.effects.map((e, i) => (
            <p key={i} className="text-muted-foreground">
              {e}
            </p>
          ))}
          <p className="mt-1 text-sm">
            {t('rcv.calls.messageSent', { text: outcome.out.message })}
          </p>
        </div>
      )}
      {list.map((c) => {
        const b2b = c.customerType === 'B2B';
        return (
          <div
            key={c.id}
            className={cn('rounded-xl border bg-card p-4', !c.decision && 'border-amber-500/60')}
          >
            <div className="flex flex-wrap items-start gap-x-8 gap-y-2">
              <div className="min-w-[14rem]">
                <div className="text-lg font-semibold">
                  {c.customerName ?? t('rcv.walkIn')}{' '}
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                    {b2b ? t('rcv.garage') : t('rcv.consumer')}
                  </span>
                </div>
                {c.customerPhone && (
                  <a
                    href={`tel:${c.customerPhone}`}
                    className="inline-flex min-h-10 items-center gap-1 text-base text-primary"
                  >
                    <Phone className="h-4 w-4" /> {c.customerPhone}
                  </a>
                )}
                <div>
                  <button
                    type="button"
                    className="min-h-10 font-semibold underline-offset-2 hover:underline"
                    onClick={() => setHistory({ id: c.orderId, number: c.orderNumber })}
                  >
                    {c.orderNumber}
                  </button>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {t('rcv.calls.closedBy', { name: c.closedBy ?? '', time: fmtTime(c.closedAt) })}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{t('rcv.calls.orderedBy')}</div>
                <div className="font-semibold">{c.orderedBy}</div>
                <div className="text-xs text-muted-foreground">{c.channelLabel}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{t('rcv.calls.part')}</div>
                <div className="font-semibold">
                  {c.missingQty} × {c.productName}
                </div>
                <div className="text-xs text-muted-foreground">
                  {c.brand} · {c.ref}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{t('rcv.calls.expectedFrom')}</div>
                <div>{c.supplierName ?? '—'}</div>
                <div className="text-sm">{t('rcv.calls.newDate', { date: fmtDay(c.newDate) })}</div>
                {c.reportNo > 1 && (
                  <div className="text-xs font-semibold text-destructive">
                    {t('rcv.ano.reports', { count: c.reportNo })}
                    {c.escalated ? ` · ${t('rcv.ano.escalated')}` : ''}
                  </div>
                )}
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{t('rcv.calls.money')}</div>
                <div>{t('rcv.calls.missingAmount', { amount: fmtEur(c.missingTTC) })}</div>
                <div className="text-sm text-muted-foreground">
                  {b2b
                    ? t('rcv.exposureOf', {
                        e: fmtEur(c.exposure),
                        l: c.creditLimit == null ? '—' : fmtEur(c.creditLimit),
                      })
                    : `${c.paymentMethod ?? t('rcv.calls.notPaid')}${c.depositInvoices ? ` · ${c.depositInvoices}` : ''}`}
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {c.decision ? (
                <div className="text-base">
                  <strong>{t(`rcv.calls.decision.${c.decision}`)}</strong>
                  <span className="text-muted-foreground">
                    {' '}
                    — {c.decidedBy ?? t('rcv.system')} {fmtTime(c.decidedAt)}
                  </span>
                  {c.decisionDetail?.docs?.map((d, i) => (
                    <span key={i} className="ml-2 rounded bg-muted px-1.5 text-sm">
                      {d.number}
                    </span>
                  ))}
                </div>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className={touch}
                    onClick={() => setDeciding({ entry: c, kind: 'WAIT' })}
                  >
                    {t('rcv.calls.wait')}
                  </Button>
                  <Button
                    variant="outline"
                    className={touch}
                    onClick={() => setDeciding({ entry: c, kind: 'SWITCH' })}
                  >
                    {t('rcv.calls.switch')}
                  </Button>
                  <Button
                    variant="destructive"
                    className={touch}
                    onClick={() => setDeciding({ entry: c, kind: 'CANCEL' })}
                  >
                    {t('rcv.calls.cancel')}
                  </Button>
                  {c.missingQty > 1 && (
                    <Button variant="ghost" className={touch} onClick={() => setPartCancel(c)}>
                      {t('rcv.calls.cancelSome')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
      <DecisionDialog
        meName={meName}
        target={deciding}
        onClose={() => setDeciding(null)}
        onDone={(entry, out) => {
          setDeciding(null);
          setOutcome({ entry, out });
          load();
        }}
      />
      <CancelLineDialog
        line={
          partCancel
            ? {
                lineId: partCancel.lineId,
                productName: partCancel.productName,
                orderNumber: partCancel.orderNumber,
                notYet: partCancel.missingQty,
              }
            : null
        }
        onClose={() => setPartCancel(null)}
        onDone={() => {
          setPartCancel(null);
          load();
        }}
      />
      <OrderHistoryDialog
        orderId={history?.id ?? null}
        orderNumber={history?.number ?? null}
        onClose={() => setHistory(null)}
      />
    </div>
  );
}

function DecisionDialog({
  meName,
  target,
  onClose,
  onDone,
}: {
  meName: string;
  target: { entry: CallEntry; kind: 'WAIT' | 'SWITCH' | 'CANCEL' } | null;
  onClose: () => void;
  onDone: (entry: CallEntry, out: DecisionOutcome) => void;
}) {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(false);
  const [alts, setAlts] = useState<Alternative[] | null>(null);
  const [supplier, setSupplier] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setConsent(false);
    setError('');
    setAlts(null);
    setSupplier('');
    if (target?.kind === 'SWITCH') {
      receivingApi
        .alternatives(target.entry.lineId)
        .then((r) => {
          setAlts(r.data);
          setSupplier(r.data[0]?.supplierId ?? '');
        })
        .catch((err) => setError(apiMessage(err)));
    }
  }, [target?.entry.lineId, target?.kind]);
  if (!target) return null;
  const { entry, kind } = target;
  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const out = await receivingApi.decide(entry.lineId, {
        decision: kind,
        supplierId: supplier || undefined,
        consent,
      });
      onDone(entry, out);
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const b2b = entry.customerType === 'B2B';
  return (
    <Modal
      open
      title={t(`rcv.calls.title.${kind}`)}
      subtitle={`${entry.customerName ?? ''} · ${entry.orderNumber} · ${entry.missingQty} × ${entry.productName} · ${fmtEur(entry.missingTTC)}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" className={touch} onClick={onClose}>
            {t('rcv.cancel')}
          </Button>
          <Button
            className={touch}
            variant={kind === 'CANCEL' ? 'destructive' : 'default'}
            disabled={busy || (kind !== 'WAIT' && !consent) || (kind === 'SWITCH' && !supplier)}
            onClick={() => void submit()}
          >
            {t('rcv.calls.record')}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-base">
        {kind === 'WAIT' && <p>{t('rcv.calls.waitText', { date: fmtDay(entry.newDate) })}</p>}
        {kind === 'CANCEL' && (
          <p>
            {b2b
              ? t('rcv.calls.cancelB2b', { amount: fmtEur(entry.missingTTC) })
              : t('rcv.calls.cancelB2c')}
          </p>
        )}
        {kind === 'SWITCH' &&
          (alts === null ? (
            <p className="text-muted-foreground">{t('rcv.loading')}</p>
          ) : alts.length === 0 ? (
            <p className="font-semibold text-destructive">{t('rcv.calls.noAlt')}</p>
          ) : (
            <fieldset className="space-y-2">
              <legend className="mb-1">{t('rcv.calls.chooseSupplier')}</legend>
              {alts.map((a) => (
                <label
                  key={a.supplierId}
                  className="flex min-h-12 items-center gap-3 rounded-lg border px-3"
                >
                  <input
                    type="radio"
                    name="alt"
                    className="h-6 w-6"
                    checked={supplier === a.supplierId}
                    onChange={() => setSupplier(a.supplierId)}
                  />
                  <span className="flex-1 font-semibold">{a.supplierName}</span>
                  <span>
                    {t('rcv.calls.altPrice', { unit: fmtEur(a.unitTTC), line: fmtEur(a.lineTTC) })}
                  </span>
                </label>
              ))}
              <p className="text-sm text-muted-foreground">
                {b2b ? t('rcv.calls.switchB2b') : t('rcv.calls.switchB2c')}
              </p>
            </fieldset>
          ))}
        {kind !== 'WAIT' && (
          <label className="flex min-h-12 items-center gap-3 rounded-lg bg-muted/50 px-3">
            <input
              type="checkbox"
              className="h-6 w-6"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            {t('rcv.calls.consentBy', { name: meName })}
          </label>
        )}
        {error && (
          <p className="text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
